import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Seo } from '@/lib/seo';
import { useAuth } from '@/lib/auth';
import { Loader } from '@/components/ui/Icons';
import { Logo } from '@/components/ui/Logo';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (opts: {
            client_id: string;
            callback: (resp: { credential: string }) => void;
            auto_select?: boolean;
            ux_mode?: 'popup' | 'redirect';
          }) => void;
          renderButton: (parent: HTMLElement, opts: Record<string, unknown>) => void;
          prompt: () => void;
        };
      };
    };
  }
}

export default function AdminLogin() {
  const { user, signInWithGoogleCredential } = useAuth();
  const location = useLocation();
  const buttonRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    const id = 'google-gis';
    if (!document.getElementById(id)) {
      const s = document.createElement('script');
      s.id = id;
      s.src = 'https://accounts.google.com/gsi/client';
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    }
    const tick = window.setInterval(() => {
      if (window.google?.accounts?.id && buttonRef.current) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: async (resp) => {
            setBusy(true);
            setError(null);
            try {
              await signInWithGoogleCredential(resp.credential);
            } catch (err) {
              const msg = err && typeof err === 'object' && 'error' in err
                ? String((err as { error: unknown }).error)
                : 'Sign-in failed.';
              setError(msg);
            } finally {
              setBusy(false);
            }
          },
          ux_mode: 'popup',
        });
        window.google.accounts.id.renderButton(buttonRef.current, {
          type: 'standard',
          shape: 'pill',
          theme: 'filled_black',
          text: 'continue_with',
          size: 'large',
          width: 320,
          logo_alignment: 'left',
        });
        window.clearInterval(tick);
      }
    }, 200);
    return () => window.clearInterval(tick);
  }, [signInWithGoogleCredential]);

  if (user) {
    const target = (location.state as { from?: string } | null)?.from ?? '/admin/quotes';
    return <Navigate to={target} replace />;
  }

  return (
    <>
      <Seo
        title="Admin sign in"
        description="Bliss Building Contractors admin sign-in."
        path="/admin/login"
        noindex
      />
      <section className="relative isolate flex min-h-screen items-center justify-center overflow-hidden bg-navy-950 px-4 py-16">
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_30%_20%,rgba(184,145,70,0.18),transparent_55%),radial-gradient(circle_at_75%_80%,rgba(39,69,112,0.45),transparent_60%)]"
        />
        <div className="absolute inset-x-0 top-0 -z-10 h-1 bg-linear-to-r from-gold-700 via-gold-400 to-gold-700" />

        <div className="w-full max-w-md rounded-2xl border border-navy-700 bg-navy-900/90 p-8 text-paper-0 shadow-2xl backdrop-blur-sm sm:p-10">
          <div className="flex flex-col items-center text-center">
            <Logo className="h-20 drop-shadow-lg" />
            <h1 className="mt-6 font-display text-2xl font-semibold tracking-tight">Admin sign in</h1>
            <p className="mt-2 text-sm text-paper-0/80">
              Use the Google account Bliss has authorised. <br />
              Sign-in is Google-only - there is no password.
            </p>
          </div>

          <div className="mt-8 flex justify-center">
            {busy ? (
              <div className="flex items-center gap-2 text-gold-300">
                <Loader className="h-5 w-5" /> Signing you in…
              </div>
            ) : (
              <div ref={buttonRef} />
            )}
          </div>

          {error && (
            <p className="mt-5 rounded-md border border-red-400/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
              {error}
            </p>
          )}

          {!GOOGLE_CLIENT_ID && (
            <p className="mt-5 rounded-md border border-yellow-300/40 bg-yellow-500/10 px-3 py-2 text-xs text-yellow-100">
              <code className="font-mono">VITE_GOOGLE_CLIENT_ID</code> isn’t set. Add it to{' '}
              <code>frontend/.env</code> and restart the dev server.
            </p>
          )}

          <p className="mt-8 text-center text-xs text-paper-0/60">
            © {new Date().getFullYear()} Bliss Building Contractors Ltd
          </p>
        </div>
      </section>
    </>
  );
}
